import { Fragment, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import {
  Dialog as HuiDialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Spinner } from "@/components/ui/Spinner";
import { Alert } from "@/components/ui/Alert";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { useCurrentUser } from "@/lib/auth";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { DialogCloseButton } from "@/components/ui/dialogCloseGuard";
import { useRealtimeSync } from "@/lib/useRealtime";
import { userCanViewBueroProjectOverviewSections } from "@/projectOverview/permissions";
import { buildSOrdnerHeadingLine } from "@/pages/projekte/projectFilterSummary";
import { SOrdnerInfoSection } from "@/sordnerOverview/SOrdnerInfoSection";
import { SOrdnerNotesSection } from "@/sordnerOverview/SOrdnerNotesSection";
import { useSOrdnerOverviewData } from "@/sordnerOverview/useSOrdnerOverviewData";
import { useModalStackLayer } from "@/lib/modalStack";

type MobilePane = "details" | "notes";

export function SOrdnerOverviewDialog({
  open,
  sordnerId,
  onClose,
}: {
  open: boolean;
  sordnerId: number | null;
  onClose: () => void;
}) {
  const { user } = useCurrentUser();
  const isMobile = useMediaQuery("(max-width: 767px)");
  const isBuero = userCanViewBueroProjectOverviewSections(user);
  const { data, loading, error, refetch } = useSOrdnerOverviewData(sordnerId, open);
  const [mobilePane, setMobilePane] = useState<MobilePane>("details");

  useEffect(() => {
    if (!open) setMobilePane("details");
  }, [open]);

  useEffect(() => {
    if (open && !isBuero) onClose();
  }, [open, isBuero, onClose]);

  useEffect(() => {
    if (!open || !sordnerId) return;
    void refetch();
  }, [open, sordnerId, refetch]);

  useRealtimeSync({
    domains: ["sordner", "auftraege"],
    enabled: open && !!sordnerId,
    onChange: () => void refetch(),
  });

  const title = useMemo(() => {
    if (!data?.sordner) return "S-Ordner";
    return `S-Ordner: ${buildSOrdnerHeadingLine(data.sordner)}`;
  }, [data]);

  const { zIndex: stackZIndex, bringToFront } = useModalStackLayer(open);

  const main = data ? <SOrdnerInfoSection data={data} onRefetch={() => void refetch()} /> : null;
  const notes = data ? (
    <SOrdnerNotesSection
      sordnerId={data.sordner.id}
      notes={data.notes}
      user={user}
      isBuero={isBuero}
      onChanged={() => void refetch()}
    />
  ) : null;

  return (
    <Transition show={open} as={Fragment}>
      <HuiDialog
        as="div"
        className="relative z-[200]"
        style={stackZIndex != null ? { zIndex: stackZIndex } : undefined}
        onClose={onClose}
        onPointerDownCapture={bringToFront}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden />
        </TransitionChild>

        <div
          className={clsx(
            "fixed inset-0",
            isMobile ? "overflow-hidden" : "overflow-y-auto",
          )}
        >
          <div
            className={clsx(
              "flex min-h-full items-center justify-center p-2 sm:p-4",
              isMobile && "h-full min-h-0 items-stretch p-0",
            )}
          >
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.99]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-100"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.99]"
            >
              <DialogPanel
                className={clsx(
                  "eh-po-dialog w-full transform overflow-hidden bg-card shadow-ios-md transition",
                  isMobile
                    ? "flex h-[100dvh] max-h-[100dvh] flex-col rounded-none"
                    : "rounded-ios-lg",
                )}
              >
                <div
                  className={clsx(
                    "flex shrink-0 items-center justify-between border-b border-separator px-4 py-3 sm:px-5",
                    isMobile && "pt-[max(0.75rem,env(safe-area-inset-top))]",
                  )}
                >
                  <DialogTitle className="min-w-0 flex-1 pr-3 text-base font-semibold text-label sm:text-lg">
                    <span className="block truncate">{title}</span>
                  </DialogTitle>
                  <DialogCloseButton onClick={onClose} />
                </div>

                {isMobile && data ? (
                  <div className="shrink-0 border-b border-separator px-4 py-2.5">
                    <SegmentedControl
                      name="S-Ordner Bereich"
                      value={mobilePane}
                      onChange={setMobilePane}
                      options={[
                        { value: "details", label: "Details" },
                        { value: "notes", label: "Notizen" },
                      ]}
                    />
                  </div>
                ) : null}

                <div
                  className={clsx(
                    "eh-po-dialog-body min-h-0 px-4 py-4 sm:px-5",
                    isMobile
                      ? "flex flex-1 flex-col overflow-hidden py-3"
                      : "max-h-[min(85vh,900px)]",
                  )}
                >
                  {loading && !data ? (
                    <div className="flex items-center justify-center py-16">
                      <Spinner />
                      <span className="ml-2 text-sm text-label-secondary">
                        Lade S-Ordner …
                      </span>
                    </div>
                  ) : error && !data ? (
                    <Alert variant="error">{error}</Alert>
                  ) : data && main && notes ? (
                    isMobile ? (
                      mobilePane === "details" ? (
                        <div className="eh-po-layout eh-po-layout--mobile min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                          <div className="eh-po-main">{main}</div>
                        </div>
                      ) : (
                        <div className="eh-po-layout eh-po-layout--mobile eh-po-layout--mobile-notes min-h-0 flex-1 overflow-hidden">
                          <div className="eh-po-notes">{notes}</div>
                        </div>
                      )
                    ) : (
                      <div className="eh-po-layout eh-po-layout--scroll-split">
                        <div className="eh-po-main eh-po-main--scroll">{main}</div>
                        <div className="eh-po-notes">{notes}</div>
                      </div>
                    )
                  ) : null}
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </HuiDialog>
    </Transition>
  );
}
